// Session Manager for handling authentication state and automatic logout

class SessionManager {
  constructor() {
    this.sessionTimeout = 8 * 60 * 60 * 1000; // 8 hours in milliseconds
    this.impersonationTimeout = 60 * 60 * 1000; // support token lifetime
    this.checkInterval = 60 * 1000; // Check every minute
    this.init();
  }

  isImpersonating() {
    try {
      const userData = JSON.parse(localStorage.getItem("userData") || "{}");
      return userData.impersonation === true;
    } catch (error) {
      return false;
    }
  }

  init() {
    // Set up event listeners for tab close and visibility change
    this.setupEventListeners();
    
    // Start session timeout checker
    this.startSessionChecker();
    
    // Check if session is expired on page load
    this.checkSessionOnLoad();
  }

  setupEventListeners() {
    // Handle page visibility change (tab switching, minimizing)
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        // Page is hidden, update last activity
        this.updateLastActivity();
      } else {
        // Page is visible again, check if session is still valid
        this.checkSessionValidity();
      }
    });

    // Handle user activity (mouse, keyboard, touch)
    const activityEvents = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    activityEvents.forEach(event => {
      document.addEventListener(event, () => {
        this.updateLastActivity();
      }, true);
    });
  }

  startSessionChecker() {
    setInterval(() => {
      this.checkSessionValidity();
    }, this.checkInterval);
  }

  checkSessionOnLoad() {
    const token = localStorage.getItem('authToken');
    if (token) {
      // Check if session is expired
      if (this.isSessionExpired()) {
        this.clearSession();
        this.redirectToLogin();
      } else {
        // Update last activity
        this.updateLastActivity();
      }
    }
  }

  checkSessionValidity() {
    const token = localStorage.getItem('authToken');
    if (token && this.isSessionExpired()) {
      this.clearSession();
      this.redirectToLogin();
    }
  }

  isSessionExpired() {
    const lastActivity = localStorage.getItem('lastActivity');
    if (!lastActivity) {
      return true;
    }

    const now = Date.now();
    const timeSinceLastActivity = now - parseInt(lastActivity);
    const timeout = this.isImpersonating()
      ? this.impersonationTimeout
      : this.sessionTimeout;

    return timeSinceLastActivity > timeout;
  }

  updateLastActivity() {
    if (this.isImpersonating()) return;
    localStorage.setItem('lastActivity', Date.now().toString());
  }

  clearSession() {
    if (this.isImpersonating()) {
      localStorage.removeItem('authToken');
      localStorage.removeItem('userData');
      localStorage.removeItem('userFullName');
      localStorage.removeItem('userProfilePic');
      localStorage.removeItem('lastActivity');
      sessionStorage.clear();
      return;
    }

    localStorage.removeItem('authToken');
    localStorage.removeItem('userData');
    localStorage.removeItem('userFullName');
    localStorage.removeItem('userProfilePic');
    localStorage.removeItem('lastActivity');
    sessionStorage.clear();
  }

  redirectToLogin() {
    // Use window.location to force a complete page reload
    if (window.location.pathname !== '/login') {
      window.location.href = '/login';
    }
  }

  // Method to be called when user logs in
  startSession(token) {
    localStorage.setItem('authToken', token);
    this.updateLastActivity();
  }

  // Method to be called when user logs out
  endSession() {
    this.clearSession();
    this.redirectToLogin();
  }

  // Check if user is authenticated
  isAuthenticated() {
    const token = localStorage.getItem('authToken');
    if (!token) return false;
    
    return !this.isSessionExpired();
  }
}

// Create singleton instance
const sessionManager = new SessionManager();

export default sessionManager; 